import { useApolloClient, useMutation } from "@apollo/client";
import {
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  AdminCreateUserAttributeMutation,
  AdminCreateUserAttributeMutationVariables,
  UserAttributeFragment,
  UserAttributeListQuery,
} from "gql/graphql";
import { useOverlay } from "modules/providers/overlay-provider";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import colors from "app/colors";
import { SERVER_ERROR } from "modules/utils/Constants";
import { getMessageFromError } from "modules/utils/Functions";
import AdminCreateUserAttributeSelect, {
  INewUserAttributeOption,
} from "./components/admin-create-user-attribute-select";
import ADMIN_CREATE_USER_ATTRIBUTE_MUTATION from "./_networking/admin-create-user-attribute";
import USER_ATTRIBUTE_LIST_QUERY from "modules/providers/user-attributes-provider/networking/user-attribute-list";
import { InfoIcon } from "modules/icons";

interface IProps {
  userAttributeCategoryId: string;
}

const AdminCreateUserAttributeModal = ({ userAttributeCategoryId }: IProps) => {
  const { closeModal } = useOverlay();
  const { cache } = useApolloClient();
  const [value, setValue] = useState<readonly INewUserAttributeOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [adminCreateUserAttributeMutationAPI] = useMutation<
    AdminCreateUserAttributeMutation,
    AdminCreateUserAttributeMutationVariables
  >(ADMIN_CREATE_USER_ATTRIBUTE_MUTATION);

  const duplicateUserAttributes = useMemo(
    () =>
      value
        .filter((v) => !!v.duplicateUserAttribute)
        .map((v) => v.duplicateUserAttribute!),
    [value],
  );

  const hasDuplicate = duplicateUserAttributes.length > 0;

  const {
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm({
    mode: "onSubmit",
  });

  const onSubmit = useCallback(async () => {
    setIsSubmitting(true);
    const results = await Promise.all(
      value.map((name) =>
        adminCreateUserAttributeMutationAPI({
          variables: {
            input: {
              name: name.value,
              userAttributeCategoryId,
            },
          },
        }),
      ),
    );
    const createdUserAttributes: UserAttributeFragment[] = results
      .map((result) => result.data?.adminCreateUserAttribute)
      .filter(
        (userAttribute): userAttribute is UserAttributeFragment =>
          !!userAttribute,
      );
    setIsSubmitting(false);
    if (createdUserAttributes) {
      cache.updateQuery<UserAttributeListQuery>(
        {
          query: USER_ATTRIBUTE_LIST_QUERY,
        },
        (data) => {
          if (!data) {
            return data;
          }
          return {
            ...data,
            userAttributeList: [
              ...data.userAttributeList,
              ...createdUserAttributes,
            ],
          };
        },
      );
    }
    closeModal();
  }, [
    adminCreateUserAttributeMutationAPI,
    cache,
    closeModal,
    userAttributeCategoryId,
    value,
  ]);

  return (
    <Modal isOpen onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Text fontWeight={600} fontSize={24} textAlign="center">
            Add User Attributes
          </Text>
        </ModalHeader>
        <ModalBody>
          <form id="admin-add-user-attribute" onSubmit={handleSubmit(onSubmit)}>
            <VStack w="full" spacing={2} alignItems="flex-start">
              <Text
                fontSize={16}
                fontWeight={700}
                color={colors.blackSecondary}
              >
                Attributes
              </Text>
              <Text fontSize={12} fontWeight={400} color={colors.gray30}>
                Type words and add a comma or hit return to set attribute
              </Text>
              <AdminCreateUserAttributeSelect
                userAttributeCategoryId={userAttributeCategoryId}
                onChange={setValue}
              />
              <Text fontSize={12} fontWeight={400} color={colors.gray30}>
                Special characters allowed: dashes, underscores and periods
              </Text>
              {hasDuplicate && (
                <HStack w="full" bg={colors.redLight} p={2} borderRadius={8}>
                  <InfoIcon fill={colors.red} />
                  <Text color={colors.red} fontWeight={500} fontSize={14}>
                    One or more attributes already exist
                  </Text>
                </HStack>
              )}
              {errors.SERVER_ERROR && (
                <HStack w="full" bg={colors.redLight} p={2} borderRadius={8}>
                  <InfoIcon fill={colors.red} />
                  <Text color={colors.red} fontWeight={500} fontSize={14}>
                    {getMessageFromError(errors?.SERVER_ERROR?.message)}
                  </Text>
                </HStack>
              )}
            </VStack>
            <Center pt={5}>
              <HStack>
                <Button
                  variant="cancel"
                  height={8}
                  w={28}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  height={8}
                  w={28}
                  onClick={() => {
                    clearErrors([SERVER_ERROR]);
                  }}
                  type="submit"
                  isDisabled={
                    isSubmitting || hasDuplicate || value.length === 0
                  }
                >
                  {isSubmitting ? <Spinner /> : "Save"}
                </Button>
              </HStack>
            </Center>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AdminCreateUserAttributeModal);
